import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import urls, { getCsrfToken } from '../com.zedock.common.script/urls.js';

import '../com.zedock.common.style/common.css';
import '../com.zedock.common.style/Header.css';

const CATEGORIES = [
    "Wooden Wall Hanging",
    "Wooden Serving Tray",
    "Homes Decoration",
    "Charming Elephant Set",
    "Wooden Dhoop Stand",
    "Floating Diya Stand",
    "Door Decoration",
    "Sculpture",
    "USB LED Night Light",
    "Metal Flower Vase",
];

const POLICIES = [
    ["Privacy Policy", "/Policies/PrivacyPolicy"],
    ["Terms & Conditions", "/Policies/TermsConditions"],
    ["Return, Refund & Cancellation Policy", "/Policies/ReturnRefundCancellationPolicy"],
    ["Shipping Policy", "/Policies/ShippingPolicy"],
];

const readCartCount = () =>
    Number(document.cookie.match(/(?:^|; )cartCount=([^;]*)/)?.[1] ?? 0) || 0;

// Call from other pages: updateCartCount(3)
export function updateCartCount(count) {
    window.dispatchEvent(new CustomEvent('zd-cart-count', { detail: count }));
}

function Header() {
    const navigate = useNavigate();
    const location = useLocation();
    const user = localStorage.getItem('userName');

    const [menuOpen, setMenuOpen] = useState(false);   // mobile menu
    const [openDd, setOpenDd] = useState(null);        // 'categories' | 'help' | null
    const [cartCount, setCartCount] = useState(readCartCount);
    const rootRef = useRef(null);

    const closeAll = () => {
        setMenuOpen(false);
        setOpenDd(null);
    };

    // close menus and refresh cart badge when the page changes
    useEffect(() => {
        closeAll();
        setCartCount(readCartCount());
    }, [location.pathname]);

    // click outside, Escape key, cart count updates
    useEffect(() => {
        const onDown = (e) => {
            if (rootRef.current && !rootRef.current.contains(e.target)) closeAll();
        };
        const onKey = (e) => {
            if (e.key === 'Escape') closeAll();
        };
        const onCart = (e) => setCartCount(Number(e.detail) || 0);

        document.addEventListener('mousedown', onDown);
        document.addEventListener('keydown', onKey);
        window.addEventListener('zd-cart-count', onCart);
        return () => {
            document.removeEventListener('mousedown', onDown);
            document.removeEventListener('keydown', onKey);
            window.removeEventListener('zd-cart-count', onCart);
        };
    }, []);

    const go = (e, path, state) => {
        e.preventDefault();
        closeAll();
        navigate(path, state ? { state } : undefined);
    };

    const handleAuth = async () => {
        closeAll();
        try {
            if (user) {
                await fetch(`${urls.BASE_URL}/logout`, {
                    method: 'POST',
                    headers: { 'X-XSRF-TOKEN': getCsrfToken() },
                    credentials: 'include',
                });
                localStorage.clear();
                navigate('/Product');
            } else {
                localStorage.clear();
                navigate('/Login', { state: { from: location } });
            }
        } catch (error) {
            // ignore
        }
    };

    const renderDropdown = (id, label, items) => (
        <li className={`zdh-dropdown${openDd === id ? ' is-open' : ''}`}>
            <button
                type="button"
                className="zdh-link zdh-caret"
                aria-haspopup="true"
                aria-expanded={openDd === id}
                onClick={() => setOpenDd(openDd === id ? null : id)}
            >
                {label}
            </button>
            <ul className="zdh-dd-menu">{items}</ul>
        </li>
    );

    return (
        <div className="container px-0">

            <header className="zdh" ref={rootRef}>
                <nav className="zdh-bar" aria-label="Main">

                    <a className="zd-brand-mark" onClick={() => navigate("/Product", { state: { category: "" } })}>
                        <img src={urls.CDN_URL + "Zedock.webp"} alt="Zedock" height={40} width={40} />
                        <span className="zd-brand-name"><span>Zedock</span> Shoppers</span>
                    </a>


                    <button
                        type="button"
                        className={`zdh-toggle${menuOpen ? ' is-open' : ''}`}
                        aria-label="Toggle navigation"
                        aria-controls="zdh-menu"
                        aria-expanded={menuOpen}
                        onClick={() => setMenuOpen((o) => !o)}
                    >
                        <span></span>
                        <span></span>
                        <span></span>
                    </button>

                    <div id="zdh-menu" className={`zdh-menu${menuOpen ? ' is-open' : ''}`}>
                        <ul className="zdh-links">
                            <li>
                                <a
                                    className="zdh-link"
                                    href="/Product"
                                    onClick={(e) => go(e, '/Product', { category: '' })}
                                >
                                    All Products
                                </a>
                            </li>

                            {renderDropdown(
                                'categories',
                                'Categories',
                                CATEGORIES.map((c) => (
                                    <li key={c}>
                                        <a
                                            className="zdh-dd-item"
                                            href="/Product"
                                            onClick={(e) => go(e, '/Product', { category: c })}
                                        >
                                            {c}
                                        </a>
                                    </li>
                                ))
                            )}

                            <li>
                                <a className="zdh-link" href="/AboutUs" onClick={(e) => go(e, '/AboutUs')}>About Us</a>
                            </li>
                            <li>
                                <a className="zdh-link" href="/ContactUs" onClick={(e) => go(e, '/ContactUs')}>Contact Us</a>
                            </li>

                            {renderDropdown(
                                'help',
                                'Help & Support',
                                POLICIES.map(([label, path]) => (
                                    <li key={path}>
                                        <a className="zdh-dd-item" href={path} onClick={(e) => go(e, path)}>
                                            {label}
                                        </a>
                                    </li>
                                ))
                            )}

                            <li>
                                <a className="zdh-link zdh-track" href="/Order" onClick={(e) => go(e, '/Order')}>Track Order</a>
                            </li>
                        </ul>

                        <div className="zdh-actions">
                            <a className="zdh-cart" href="/Cart" aria-label="Cart" onClick={(e) => go(e, '/Cart')}>
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                    <circle cx="9" cy="21" r="1"></circle>
                                    <circle cx="20" cy="21" r="1"></circle>
                                    <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
                                </svg>
                                {cartCount > 0 && <span className="zdh-badge">{cartCount}</span>}
                            </a>

                            <button type="button" className="zdh-btn zdh-btn-outline" onClick={handleAuth}>
                                {user ? 'Logout' : 'Login'}
                            </button>

                            {!user && (
                                <button type="button" className="zdh-btn zdh-btn-solid" onClick={(e) => go(e, '/SignUp')}>
                                    Sign Up
                                </button>
                            )}
                        </div>
                    </div>
                </nav>
            </header>
        </div>
    );
}

export default Header;
